import type { UVGenerator } from 'three'
import {
  DoubleSide,
  ExtrudeGeometry,
  Group,
  LinearSRGBColorSpace,
  Mesh,
  MeshBasicMaterial,
  MeshStandardMaterial,
  RepeatWrapping,
  Shape,
  ShapeGeometry,
  TextureLoader,
  Vector2,
} from 'three'

import { getResourceUrl } from '../systems/resources'

const stairWoodUVGenerator: UVGenerator = {
  generateTopUV: (_geometry, vertices, indexA, indexB, indexC) => {
    const aX = vertices[indexA * 3] ?? 0
    const aY = vertices[indexA * 3 + 1] ?? 0
    const bX = vertices[indexB * 3] ?? 0
    const bY = vertices[indexB * 3 + 1] ?? 0
    const cX = vertices[indexC * 3] ?? 0
    const cY = vertices[indexC * 3 + 1] ?? 0

    const scale = 0.0025

    return [
      new Vector2(aX * scale, aY * scale),
      new Vector2(bX * scale, bY * scale),
      new Vector2(cX * scale, cY * scale),
    ]
  },

  generateSideWallUV: () => {
    const u1 = 0
    const u2 = 0
    const u3 = 1
    const u4 = 1

    const v1 = 0
    const v2 = 1
    const v3 = 1
    const v4 = 0

    return [new Vector2(u1, v1), new Vector2(u2, v2), new Vector2(u3, v3), new Vector2(u4, v4)]
  },
}

interface StairsProps {
  width: number
  numberOfSteps: number
  riserDistance: number
  goingDistance: number
  leftBalustrade: boolean
  rightBalustrade: boolean
}

const woodTexture = new TextureLoader().load(getResourceUrl('/models/dark_wood.jpg'), texture => {
  texture.colorSpace = LinearSRGBColorSpace
  woodTexture.wrapS = RepeatWrapping
  woodTexture.wrapT = RepeatWrapping

  woodTexture.repeat.set(1, 1)
})

export const createExtrudedStairsObject = (props: StairsProps) => {
  const { width, numberOfSteps, riserDistance, goingDistance } = props

  const material = new MeshStandardMaterial({
    color: 0x6b4f27,
    roughness: 0.8,
    metalness: 0,
    name: 'stairs',
    map: woodTexture,
    roughnessMap: woodTexture,
  })

  const deskThickness = 250
  const deskDepth = 500
  const panelOffset = goingDistance * 1.2
  const totalDepth = (numberOfSteps - 1) * goingDistance
  const totalHeight = numberOfSteps * riserDistance

  const shape = new Shape()
  shape.moveTo(-300, -200)
  shape.lineTo(-300, 0)
  shape.lineTo(0, 0)

  for (let i = 0; i < numberOfSteps - 1; i++) {
    const stepOffset = i * goingDistance
    const stepTotalHeight = (i + 1) * riserDistance

    shape.lineTo(stepOffset, stepTotalHeight)
    shape.lineTo(stepOffset + goingDistance, stepTotalHeight)
  }

  shape.lineTo(totalDepth, totalHeight)
  shape.lineTo(totalDepth + deskDepth, totalHeight)
  shape.lineTo(totalDepth + deskDepth, totalHeight - deskThickness)
  shape.lineTo(totalDepth, totalHeight - deskThickness)
  shape.lineTo(panelOffset, 0)
  shape.lineTo(panelOffset, -200)
  shape.lineTo(-300, -200)

  const extrudeSettings = {
    steps: 1,
    depth: width,
    bevelEnabled: false,
    UVGenerator: stairWoodUVGenerator,
  }

  const geometry = new ExtrudeGeometry(shape, extrudeSettings)
  const stairsMesh = new Mesh(geometry, material)

  stairsMesh.position.set(0.01, 0.01, totalDepth + 0.01)
  stairsMesh.rotateY(Math.PI / 2)

  return stairsMesh
}

export const createStairsSketchingObject = (props: StairsProps) => {
  const { width, numberOfSteps, goingDistance, leftBalustrade, rightBalustrade } = props

  const depth = (numberOfSteps - 1) * goingDistance

  const lineWidth = 100
  const lineColor = 0x000000
  const fadedLineColor = 0x888888

  const lineShape = new Shape()

  lineShape.moveTo(lineWidth, 0)
  lineShape.lineTo(width - lineWidth, 0)
  lineShape.lineTo(width - lineWidth, depth)
  lineShape.lineTo(lineWidth, depth)
  lineShape.lineTo(lineWidth, 0)

  // Create step boxes with gradient shading
  const stepGroup = new Group()

  for (let i = 0; i < numberOfSteps - 1; i++) {
    const stepY = i * goingDistance
    const stepEndY = (i + 1) * goingDistance
    const stepBoxShape = new Shape()

    stepBoxShape.moveTo(lineWidth, stepY)
    stepBoxShape.lineTo(width - lineWidth, stepY)
    stepBoxShape.lineTo(width - lineWidth, stepEndY)
    stepBoxShape.lineTo(lineWidth, stepEndY)
    stepBoxShape.lineTo(lineWidth, stepY)

    const gradientFactor = i / (numberOfSteps - 1)
    const baseGray = 0xb8
    const darkGray = 0x6a
    const grayValue = Math.round(baseGray - (baseGray - darkGray) * gradientFactor)
    const stepColor = (grayValue << 16) | (grayValue << 8) | grayValue

    const stepGeometry = new ShapeGeometry(stepBoxShape)
    const stepMaterial = new MeshBasicMaterial({ color: stepColor, side: DoubleSide })
    const stepMesh = new Mesh(stepGeometry, stepMaterial)
    stepGroup.add(stepMesh)
  }

  const leftLineShape = new Shape()

  leftLineShape.moveTo(0, 0)
  leftLineShape.lineTo(0, depth)
  leftLineShape.lineTo(lineWidth, depth)
  leftLineShape.lineTo(lineWidth, 0)
  leftLineShape.lineTo(0, 0)

  const leftGeometry = new ShapeGeometry(leftLineShape)
  const leftMaterial = new MeshBasicMaterial({
    color: leftBalustrade ? lineColor : fadedLineColor,
    side: DoubleSide,
    transparent: !leftBalustrade,
  })
  const leftMesh = new Mesh(leftGeometry, leftMaterial)

  const rightLineShape = new Shape()

  rightLineShape.moveTo(width - lineWidth, 0)
  rightLineShape.lineTo(width - lineWidth, depth)
  rightLineShape.lineTo(width, depth)
  rightLineShape.lineTo(width, 0)
  rightLineShape.lineTo(width - lineWidth, 0)

  const rightGeometry = new ShapeGeometry(rightLineShape)
  const rightMaterial = new MeshBasicMaterial({
    color: rightBalustrade ? lineColor : fadedLineColor,
    side: DoubleSide,
    transparent: !rightBalustrade,
  })
  const rightMesh = new Mesh(rightGeometry, rightMaterial)

  const arrowShape = new Shape()
  const arrowWidth = 6
  const arrowHeadLength = 100
  const centerX = width / 2
  const arrowBodyLength = depth - arrowHeadLength
  const arrowHeadWidth = 35
  const depthOffset = 25

  arrowShape.moveTo(centerX - arrowWidth / 2, depth - depthOffset)
  arrowShape.lineTo(centerX - arrowWidth / 2, depth - (arrowBodyLength - 2 * depthOffset))
  arrowShape.lineTo(centerX - arrowHeadWidth, depth - (arrowBodyLength - 2 * depthOffset))
  arrowShape.lineTo(centerX, depthOffset + 2 * depthOffset)
  arrowShape.lineTo(centerX + arrowHeadWidth, depth - (arrowBodyLength - 2 * depthOffset))
  arrowShape.lineTo(centerX + arrowWidth / 2, depth - (arrowBodyLength - 2 * depthOffset))
  arrowShape.lineTo(centerX + arrowWidth / 2, depth - depthOffset)
  arrowShape.lineTo(centerX - arrowWidth / 2, depth - depthOffset)

  const arrowGeometry = new ShapeGeometry(arrowShape)
  const arrowMaterial = new MeshBasicMaterial({ color: lineColor, side: DoubleSide })
  const arrowMesh = new Mesh(arrowGeometry, arrowMaterial)

  arrowMesh.position.z = -20

  const group = new Group()
  group.add(stepGroup)
  group.add(leftMesh)
  group.add(rightMesh)
  group.add(arrowMesh)

  group.rotateX(Math.PI / 2)
  group.position.set(0, -20, 0)

  return group
}
